<template>
  <div class="cv" :class="`theme-${theme}`" :lang="locale === 'pt' ? 'pt-BR' : 'en'">
    <!-- ─── Barra fixa: seções + idioma + tema ──────────────── -->
    <nav class="cv-bar" :class="{ 'is-scrolled': isScrolled }" :aria-label="$t('cvPage.navLabel')">
      <div class="wrap bar-inner">
        <a href="#top" class="bar-name" @click.prevent="scrollToTop">{{ $t('cvPage.name') }}</a>

        <ul class="bar-links">
          <li v-for="section in sections" :key="section">
            <a
              :href="`#${section}`"
              :class="{ active: activeSection === section }"
              :aria-current="activeSection === section ? 'true' : null"
              @click.prevent="scrollToSection(section)"
            >{{ $t(`cvPage.sections.${section}`) }}</a>
          </li>
        </ul>

        <div class="bar-tools">
          <div class="lang-switch" role="group" :aria-label="$t('cvPage.langLabel')">
            <button type="button" :aria-pressed="locale === 'pt'" @click="setLang('pt')">PT</button>
            <span aria-hidden="true">/</span>
            <button type="button" :aria-pressed="locale === 'en'" @click="setLang('en')">EN</button>
          </div>
          <button
            type="button"
            class="theme-toggle"
            :aria-label="theme === 'dark' ? $t('cvPage.theme.toLight') : $t('cvPage.theme.toDark')"
            :title="theme === 'dark' ? $t('cvPage.theme.toLight') : $t('cvPage.theme.toDark')"
            @click="toggleTheme"
          >
            <!-- Sol no modo escuro (vai para o claro), lua no modo claro -->
            <svg v-if="theme === 'dark'" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2.5v2 M12 19.5v2 M2.5 12h2 M19.5 12h2 M5.3 5.3l1.4 1.4 M17.3 17.3l1.4 1.4 M5.3 18.7l1.4-1.4 M17.3 6.7l1.4-1.4" />
            </svg>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
            </svg>
          </button>
        </div>
      </div>
    </nav>

    <main id="top" class="cv-main">
      <!-- ─── Cabeçalho ──────────────────────────────────────── -->
      <header class="cv-header wrap">
        <img
          class="portrait"
          :src="portrait"
          :alt="$t('cvPage.photoAlt')"
          width="250"
          height="250"
        />
        <div class="identity">
          <h1 class="name">{{ $t('cvPage.name') }}</h1>
          <p class="headline">
            {{ $t('cvPage.headline.field') }}<span class="headline-sep" aria-hidden="true">|</span>{{ $t('cvPage.headline.focus') }}
          </p>
          <ul class="contact-line">
            <li>{{ $t('cvPage.location') }}</li>
            <li><a :href="`mailto:${contactEmail}`">{{ $t('cvPage.links.email') }}</a></li>
            <li><a :href="linkedinUrl" target="_blank" rel="noopener noreferrer">{{ $t('cvPage.links.linkedin') }}</a></li>
            <li><router-link to="/projects">{{ $t('cvPage.links.portfolio') }}</router-link></li>
          </ul>
          <!-- Na impressão os links viram texto legível -->
          <p class="print-only print-contact">
            {{ $t('cvPage.location') }} · {{ contactEmail }} · {{ linkedinUrl.replace('https://www.', '') }} · gusmachado.com.br
          </p>
        </div>
      </header>

      <!-- ─── 01 Resumo ──────────────────────────────────────── -->
      <section id="summary" class="cv-section" data-section aria-labelledby="h-summary">
        <div class="wrap section-grid">
          <h2 id="h-summary" class="section-head">
            <span class="section-no">01</span>{{ $t('cvPage.sections.summary') }}
          </h2>
          <div class="section-body summary">
            <div class="summary-text">
              <p v-for="(paragraph, index) in summaryParagraphs" :key="index">{{ paragraph }}</p>
            </div>
            <dl class="facts">
              <div v-for="fact in facts" :key="fact" class="fact">
                <dt>{{ $t(`cvPage.facts.${fact}.label`) }}</dt>
                <dd>{{ $t(`cvPage.facts.${fact}.value`) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <!-- ─── 02 Experiência ─────────────────────────────────── -->
      <section id="experience" class="cv-section" data-section aria-labelledby="h-experience">
        <div class="wrap section-grid">
          <h2 id="h-experience" class="section-head">
            <span class="section-no">02</span>{{ $t('cvPage.sections.experience') }}
          </h2>
          <div class="section-body">
            <article v-for="job in experience" :key="job.key" class="job">
              <div class="job-meta">
                <p class="job-period">{{ formatPeriod(job.period) }}</p>
                <p class="job-place">{{ $t(`cvPage.experience.${job.key}.place`) }}</p>
              </div>
              <div class="job-content">
                <h3 class="job-company">{{ $t(`cvPage.experience.${job.key}.company`) }}</h3>
                <p class="job-role">{{ $t(`cvPage.experience.${job.key}.role`) }}</p>
                <p class="job-summary">{{ $t(`cvPage.experience.${job.key}.summary`) }}</p>
                <ul class="job-points">
                  <li v-for="(point, index) in listOf(`cvPage.experience.${job.key}.points`)" :key="index">{{ point }}</li>
                </ul>
                <div v-if="job.partners" class="partners">
                  <p class="micro-label">{{ $t('cvPage.experience.gusMachado.partners') }}</p>
                  <ul class="partner-logos">
                    <li v-for="brand in brands" :key="brand.name">
                      <img :src="brand.src" :alt="brand.name" loading="lazy" decoding="async" draggable="false" />
                    </li>
                  </ul>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <!-- ─── 03 Projetos ────────────────────────────────────── -->
      <section id="projects" class="cv-section" data-section aria-labelledby="h-projects">
        <div class="wrap section-grid">
          <h2 id="h-projects" class="section-head">
            <span class="section-no">03</span>{{ $t('cvPage.sections.projects') }}
          </h2>
          <div class="section-body">
            <div class="project-filters" role="tablist" :aria-label="$t('cvPage.projects.filterLabel')">
              <button
                v-for="filter in filters"
                :key="filter"
                type="button"
                role="tab"
                :aria-selected="activeFilter === filter"
                :class="{ active: activeFilter === filter }"
                @click="activeFilter = filter"
              >
                {{ $t(`cvPage.projects.filters.${filter}`) }}<span class="filter-count">{{ countFor(filter) }}</span>
              </button>
            </div>
            <p class="sr-only" aria-live="polite">{{ $t('cvPage.projects.count', visibleProjectCount) }}</p>

            <article v-for="group in visibleGroups" :key="group.key" class="project-group">
              <header class="group-head">
                <h3 class="group-title">{{ $t(`cvPage.projects.groups.${group.key}.title`) }}</h3>
                <p class="group-meta">
                  {{ $t(`cvPage.projects.groups.${group.key}.place`) }} · {{ formatPeriod(group.period) }}
                </p>
              </header>

              <div class="group-role">
                <div>
                  <p class="micro-label">{{ $t('cvPage.projects.myRole') }}</p>
                  <p class="group-role-title">{{ $t(`cvPage.projects.groups.${group.key}.role`) }}</p>
                  <p class="group-summary">{{ $t(`cvPage.projects.groups.${group.key}.summary`) }}</p>
                </div>
                <div>
                  <p class="micro-label">{{ $t('cvPage.projects.responsibilities') }}</p>
                  <ul class="group-points">
                    <li v-for="(point, index) in listOf(`cvPage.projects.groups.${group.key}.points`)" :key="index">{{ point }}</li>
                  </ul>
                </div>
              </div>

              <ul class="project-list">
                <li v-for="project in group.projects" :key="project.id">
                  <button
                    type="button"
                    class="project"
                    :aria-label="`${$t('cvPage.projects.details')}: ${$t(`cvPage.projects.items.${project.id}.name`)}`"
                    @click="openProject(project, $event)"
                  >
                    <span class="project-media">
                      <img
                        :src="project.image"
                        :alt="$t(`cvPage.projects.items.${project.id}.name`)"
                        :style="{ objectPosition: project.imagePosition }"
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                    <span class="project-head">
                      <span class="project-name">{{ $t(`cvPage.projects.items.${project.id}.name`) }}</span>
                      <span class="project-year">{{ formatPeriod(project.period) }}</span>
                    </span>
                    <span class="project-type">{{ $t(`cvPage.projects.items.${project.id}.type`) }}</span>
                    <span v-if="project.highlight" class="project-highlight">
                      {{ $t(`cvPage.projects.items.${project.id}.highlight`) }}
                    </span>
                    <span class="project-more">{{ $t('cvPage.projects.details') }} →</span>
                  </button>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <!-- ─── 04 Formação ────────────────────────────────────── -->
      <section id="education" class="cv-section" data-section aria-labelledby="h-education">
        <div class="wrap section-grid">
          <h2 id="h-education" class="section-head">
            <span class="section-no">04</span>{{ $t('cvPage.sections.education') }}
          </h2>
          <div class="section-body">
            <article v-for="edu in education" :key="edu.key" class="job">
              <div class="job-meta">
                <p class="job-period">{{ formatPeriod(edu.period) }}</p>
                <p class="job-place">{{ $t(`cvPage.education.${edu.key}.place`) }}</p>
              </div>
              <div class="job-content">
                <h3 class="job-company">{{ $t(`cvPage.education.${edu.key}.school`) }}</h3>
                <p class="job-role">{{ $t(`cvPage.education.${edu.key}.degree`) }}</p>
                <p v-if="edu.status" class="job-summary">{{ $t(`cvPage.education.${edu.key}.status`) }}</p>
                <router-link v-if="edu.exhibit" :to="edu.exhibit" class="text-link">
                  {{ $t(`cvPage.education.${edu.key}.note`) }} →
                </router-link>
              </div>
            </article>
          </div>
        </div>
      </section>

      <!-- ─── 05 Competências ────────────────────────────────── -->
      <section id="skills" class="cv-section" data-section aria-labelledby="h-skills">
        <div class="wrap section-grid">
          <h2 id="h-skills" class="section-head">
            <span class="section-no">05</span>{{ $t('cvPage.sections.skills') }}
          </h2>
          <dl class="section-body skills">
            <div v-for="group in skillGroups" :key="group" class="skill-row">
              <dt>{{ $t(`cvPage.skills.${group}.title`) }}</dt>
              <dd>
                <ul class="inline-list">
                  <li v-for="(item, index) in listOf(`cvPage.skills.${group}.items`)" :key="index">{{ item }}</li>
                </ul>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <!-- ─── 06 Idiomas ─────────────────────────────────────── -->
      <section id="languages" class="cv-section" data-section aria-labelledby="h-languages">
        <div class="wrap section-grid">
          <h2 id="h-languages" class="section-head">
            <span class="section-no">06</span>{{ $t('cvPage.sections.languages') }}
          </h2>
          <dl class="section-body languages">
            <div v-for="lang in languages" :key="lang" class="skill-row">
              <dt>{{ $t(`cvPage.languages.${lang}.name`) }}</dt>
              <dd>{{ $t(`cvPage.languages.${lang}.level`) }}</dd>
            </div>
          </dl>
        </div>
      </section>
    </main>

    <FooterGusMachadoYear class="cv-footer" />

    <!-- ─── Detalhes do projeto ──────────────────────────────── -->
    <Teleport to="body">
      <transition name="cv-modal">
        <div
          v-if="selectedProject"
          class="cv-modal-backdrop"
          :class="{ 'is-light': theme === 'light' }"
          @click.self="closeProject"
        >
          <div
            class="cv-modal"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="`modal-title-${selectedProject.id}`"
            :lang="locale === 'pt' ? 'pt-BR' : 'en'"
          >
            <button ref="closeButton" type="button" class="cv-modal-close" :aria-label="$t('cvPage.projects.close')" @click="closeProject">×</button>

            <div class="cv-modal-media" :class="{ instagram: isInstagram(selectedVideo) }">
              <iframe
                v-if="selectedVideo"
                :key="selectedVideo"
                :src="toEmbedUrl(selectedVideo)"
                :title="$t('cvPage.projects.video')"
                frameborder="0"
                scrolling="no"
                allow="autoplay; encrypted-media; picture-in-picture; clipboard-write"
                allowfullscreen
              ></iframe>
              <img
                v-else
                :src="selectedProject.image"
                :alt="$t(`cvPage.projects.items.${selectedProject.id}.name`)"
                :style="{ objectPosition: selectedProject.imagePosition }"
              />
            </div>

            <div class="cv-modal-body">
              <p class="cv-modal-context">
                {{ $t(`cvPage.projects.groups.${selectedProject.group}.title`) }} · {{ formatPeriod(selectedProject.period) }}
              </p>
              <h3 :id="`modal-title-${selectedProject.id}`" class="cv-modal-title">
                {{ $t(`cvPage.projects.items.${selectedProject.id}.name`) }}
              </h3>
              <p class="cv-modal-type">
                {{ $t(`cvPage.projects.items.${selectedProject.id}.type`) }} · {{ $t(`cvPage.projects.items.${selectedProject.id}.place`) }}
              </p>
              <p v-if="selectedProject.highlight" class="cv-modal-highlight">
                {{ $t(`cvPage.projects.items.${selectedProject.id}.highlight`) }}
              </p>
              <p class="cv-modal-desc">{{ $t(`cvPage.projects.items.${selectedProject.id}.desc`) }}</p>

              <dl class="cv-modal-facts">
                <div>
                  <dt>{{ $t('cvPage.projects.myRole') }}</dt>
                  <dd>{{ $t(`cvPage.projects.groups.${selectedProject.group}.role`) }}</dd>
                </div>
                <div v-if="selectedSpecs.length">
                  <dt>{{ $t('cvPage.projects.specs') }}</dt>
                  <dd>
                    <ul class="spec-list">
                      <li v-for="(spec, index) in selectedSpecs" :key="index">{{ spec }}</li>
                    </ul>
                  </dd>
                </div>
                <div v-if="selectedProject.tools">
                  <dt>{{ $t('cvPage.projects.tools') }}</dt>
                  <dd>{{ $t(`cvPage.projects.items.${selectedProject.id}.tools`) }}</dd>
                </div>
              </dl>

              <p v-if="hasLinks(selectedProject)" class="cv-modal-links">
                <a v-if="selectedProject.links?.rcdb" :href="selectedProject.links.rcdb" target="_blank" rel="noopener noreferrer">
                  {{ $t('projectsPage.rcdb') }} ↗
                </a>
                <a v-if="selectedProject.links?.hotel" :href="selectedProject.links.hotel" target="_blank" rel="noopener noreferrer">
                  {{ $t('projectsPage.hotel') }} ↗
                </a>
                <router-link v-if="selectedProject.exhibit" :to="selectedProject.exhibit" @click="closeProject">
                  {{ $t('cvPage.projects.exhibit') }} →
                </router-link>
              </p>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import FooterGusMachadoYear from '@/components/Footer-GusMachadoYear.vue'

import portrait from '@/assets/logo-rs-gusmachado.jpg'
import benditoCampos from '@/assets/carrossel-projetos/bendito-campos.jpg'
import benditoLindoia from '@/assets/carrossel-projetos/bendito-lindoia.jpg'
import benAzelart from '@/assets/carrossel-projetos/ben-azelart-coaster.jpg'
import eurekaImg from '@/assets/carrossel-projetos/eureka.jpg'
import contratempoImg from '@/assets/carrossel-projetos/contratempo.jpg'
import omashuCover from '@/assets/omashu/historia/construcao-geral.jpg'
import dreamsImg from '@/assets/cv/dreams.jpg'
import horusImg from '@/assets/cv/horus.jpg'
import mistralImg from '@/assets/cv/mistral.jpg'
import enigmaukaImg from '@/assets/cv/enigmauka.jpg'
import typhoonImg from '@/assets/cv/typhoon-360.jpg'

import netflixLogo from '@/assets/cv/marcas/Netflix.png'
import redBullLogo from '@/assets/cv/marcas/RedBull.png'
import havanLogo from '@/assets/cv/marcas/Havan.png'
import einhellLogo from '@/assets/cv/marcas/Einhell.png'
import dorflexLogo from '@/assets/cv/marcas/Dorflex.png'
import kanitLogo from '@/assets/cv/marcas/Kanit.png'
import desktopLogo from '@/assets/cv/marcas/Desktop.png'
import ressoLogo from '@/assets/cv/marcas/Resso.png'
import banggoodLogo from '@/assets/cv/marcas/Banggood.png'

const { t, tm, rt, locale } = useI18n()
const route = useRoute()

const contactEmail = 'contato@gusmachado.com.br'
const linkedinUrl = 'https://www.linkedin.com/in/gusmachadorc/'

// Preferências salvas no navegador; sem armazenamento disponível, valem só nesta visita
const readStorage = (key) => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Ignora: o navegador bloqueou o armazenamento
  }
}

const setLang = (lang) => {
  locale.value = lang
  writeStorage('lang', lang)
}

// ─── Tema claro / escuro (padrão: escuro, a identidade do site) ─
const theme = ref(readStorage('theme') === 'light' ? 'light' : 'dark')

const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark'
  writeStorage('theme', theme.value)
}

// ─── Barra fixa: fica mais discreta depois que a página rola ─
const isScrolled = ref(false)
const handleScroll = () => {
  isScrolled.value = window.scrollY > 24
}

// Listas do i18n (arrays) já resolvidas no idioma atual
const listOf = (key) => tm(key).map((item) => rt(item))

const formatPeriod = ([start, end]) => {
  if (start === end) return String(start)
  return `${start} – ${end ?? t('cvPage.present')}`
}

// ─── Resumo ───────────────────────────────────────────────
const summaryParagraphs = computed(() => listOf('cvPage.summary'))
const facts = ['role', 'rides', 'coasters', 'audience']

// ─── Experiência / Formação ───────────────────────────────
const experience = [
  { key: 'cacauPark', period: [2025, null] },
  { key: 'cacauShow', period: [2024, null] },
  { key: 'gusMachado', period: [2020, null], partners: true }
]

const brands = [
  { name: 'Netflix', src: netflixLogo },
  { name: 'Red Bull', src: redBullLogo },
  { name: 'Havan', src: havanLogo },
  { name: 'Einhell', src: einhellLogo },
  { name: 'Dorflex', src: dorflexLogo },
  { name: 'Kanit', src: kanitLogo },
  { name: 'Desktop', src: desktopLogo },
  { name: 'Resso', src: ressoLogo },
  { name: 'Banggood', src: banggoodLogo }
]

const education = [
  { key: 'unip', period: [2025, null], status: true, exhibit: '/exposicoes/omashu-express-unip' },
  { key: 'mackenzie', period: [2020, 2024] }
]

const skillGroups = ['mechanical', 'cad', 'installation', 'management', 'documentation', 'standards', 'media']
const languages = ['pt', 'en']

// ─── Projetos ─────────────────────────────────────────────
const specKeys = ['speed', 'extension', 'height']

const projects = [
  {
    id: 'dreams',
    group: 'cacauPark',
    period: [2025, null],
    highlight: true,
    image: dreamsImg,
    imagePosition: 'center 60%',
    // Vídeo diferente para cada idioma do site
    video: {
      pt: 'https://www.instagram.com/p/DZcxf4YluC3/',
      en: 'https://www.instagram.com/p/DcRW5xQISOG/'
    },
    specs: true,
    links: { rcdb: 'https://rcdb.com/22336.htm' }
  },
  {
    id: 'mistral',
    group: 'cacauPark',
    period: [2025, null],
    image: mistralImg,
    imagePosition: 'center 35%',
    specs: true,
    links: { rcdb: 'https://rcdb.com/22333.htm' }
  },
  {
    id: 'horus',
    group: 'cacauPark',
    period: [2025, null],
    image: horusImg,
    imagePosition: 'center 30%',
    specs: true,
    links: { rcdb: 'https://rcdb.com/22332.htm' }
  },
  {
    id: 'typhoon',
    group: 'cacauPark',
    period: [2026, 2026],
    highlight: true,
    image: typhoonImg,
    imagePosition: 'center 55%',
    video: 'https://www.instagram.com/p/DaTpYzhiWcJ/'
  },
  { id: 'enigmauka', group: 'cacauPark', period: [2025, 2026], highlight: true, image: enigmaukaImg, imagePosition: 'center 45%' },
  {
    id: 'benditoCampos',
    group: 'cacauShow',
    period: [2024, 2025],
    image: benditoCampos,
    video: 'https://www.youtube.com/shorts/TEUZ0Y4jNuc',
    techKey: 'oompamobile2',
    links: { hotel: 'https://www.benditocacaoresort.com.br/', rcdb: 'https://rcdb.com/22982.htm' }
  },
  {
    id: 'benditoLindoia',
    group: 'cacauShow',
    period: [2024, 2024],
    image: benditoLindoia,
    video: 'https://www.youtube.com/watch?v=Fv61MQ5k7no',
    techKey: 'oompamobile1',
    links: { hotel: 'https://www.benditocacaoresort.com.br/', rcdb: 'https://rcdb.com/22433.htm' }
  },
  {
    id: 'omashu',
    group: 'independent',
    period: [2026, 2026],
    highlight: true,
    tools: true,
    image: omashuCover,
    video: 'https://www.youtube.com/watch?v=f59654RmbIc',
    techKey: 'omashuExpress',
    exhibit: '/exposicoes/omashu-express-unip'
  },
  { id: 'benAzelart', group: 'independent', period: [2024, 2024], image: benAzelart, video: 'https://www.youtube.com/watch?v=02ex9hBiXNI', techKey: 'benazelart' },
  { id: 'eureka', group: 'independent', period: [2022, 2023], image: eurekaImg, video: 'https://www.youtube.com/shorts/N2pICVB5oh4', techKey: 'eurekaV1' },
  { id: 'contratempo', group: 'independent', period: [2018, 2021], image: contratempoImg, video: 'https://www.youtube.com/watch?v=QtOuFWwOsvU', techKey: 'contratempo1' }
]

const groupKeys = ['cacauPark', 'cacauShow', 'independent']
const filters = ['all', ...groupKeys]
const activeFilter = ref('all')

// Período do grupo = do primeiro ano ao último (ou "Atual" se algum segue em andamento)
const groups = groupKeys.map((key) => {
  const list = projects.filter((project) => project.group === key)
  const start = Math.min(...list.map((project) => project.period[0]))
  const ongoing = list.some((project) => project.period[1] === null)
  const end = ongoing ? null : Math.max(...list.map((project) => project.period[1]))
  return { key, projects: list, period: [start, end] }
})

const visibleGroups = computed(() =>
  activeFilter.value === 'all' ? groups : groups.filter((group) => group.key === activeFilter.value)
)

const visibleProjectCount = computed(() =>
  visibleGroups.value.reduce((total, group) => total + group.projects.length, 0)
)

const countFor = (filter) =>
  filter === 'all' ? projects.length : projects.filter((project) => project.group === filter).length

const hasLinks = (project) => !!(project.links?.rcdb || project.links?.hotel || project.exhibit)

// ─── Vídeos (YouTube e Instagram) ─────────────────────────
const instagramRegex = /instagram\.com\/(?:p|reel|reels|tv)\/([\w-]+)/

const isInstagram = (url) => !!url && instagramRegex.test(url)

const toEmbedUrl = (url) => {
  const instagram = url.match(instagramRegex)
  if (instagram) return `https://www.instagram.com/p/${instagram[1]}/embed`

  const match = url.match(/(?:v=|shorts\/|youtu\.be\/|embed\/)([\w-]{11})/)
  return match ? `https://www.youtube-nocookie.com/embed/${match[1]}?rel=0&modestbranding=1` : url
}

const selectedProject = ref(null)
const closeButton = ref(null)
let lastTrigger = null

// O vídeo pode ser um link único ou um link por idioma ({ pt, en })
const selectedVideo = computed(() => {
  const video = selectedProject.value?.video
  if (!video || typeof video === 'string') return video || ''
  return video[locale.value] || video.pt || video.en || ''
})

/*
  Ficha técnica: projetos próprios reaproveitam os dados da página de Projetos
  (techKey); as atrações do Cacau Park têm a lista no próprio cvPage (dados do RCDB).
*/
const selectedSpecs = computed(() => {
  const project = selectedProject.value
  if (!project) return []
  if (project.techKey) return specKeys.map((spec) => t(`projectsPage.techInfo.${project.techKey}.${spec}`))
  if (project.specs) return listOf(`cvPage.projects.items.${project.id}.specs`)
  return []
})

const openProject = async (project, event) => {
  lastTrigger = event?.currentTarget ?? null
  selectedProject.value = project
  document.body.style.overflow = 'hidden'
  await nextTick()
  closeButton.value?.focus()
}

const closeProject = () => {
  selectedProject.value = null
  document.body.style.overflow = ''
  lastTrigger?.focus?.()
  lastTrigger = null
}

const handleKeydown = (e) => {
  if (e.key === 'Escape' && selectedProject.value) closeProject()
}

// ─── Navegação entre seções ───────────────────────────────
const sections = ['summary', 'experience', 'projects', 'education', 'skills', 'languages']
const activeSection = ref('')

const scrollToSection = (id) => {
  const el = document.getElementById(id)
  if (!el) return
  // Desconta a altura da barra fixa
  const barHeight = document.querySelector('.cv-bar')?.offsetHeight ?? 0
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - barHeight + 1, behavior: 'smooth' })
}

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

// ─── SEO: título, descrição, idioma do documento e dados estruturados ─
let previousMeta = null

const applyMeta = () => {
  document.title = t('cvPage.meta.title')
  document.documentElement.lang = locale.value === 'pt' ? 'pt-BR' : 'en'

  let description = document.querySelector('meta[name="description"]')
  if (!description) {
    description = document.createElement('meta')
    description.setAttribute('name', 'description')
    document.head.appendChild(description)
  }
  description.setAttribute('content', t('cvPage.meta.description'))

  let jsonLd = document.getElementById('cv-jsonld')
  if (!jsonLd) {
    jsonLd = document.createElement('script')
    jsonLd.type = 'application/ld+json'
    jsonLd.id = 'cv-jsonld'
    document.head.appendChild(jsonLd)
  }
  jsonLd.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: t('cvPage.name'),
    alternateName: 'Gus Machado',
    jobTitle: t('cvPage.experience.cacauPark.role'),
    worksFor: { '@type': 'Organization', name: 'Cacau Park' },
    address: { '@type': 'PostalAddress', addressLocality: 'Sorocaba', addressCountry: 'BR' },
    email: `mailto:${contactEmail}`,
    url: 'https://www.gusmachado.com.br/cv',
    sameAs: [linkedinUrl]
  })
}

const restoreMeta = () => {
  if (!previousMeta) return
  document.documentElement.lang = previousMeta.lang
  const description = document.querySelector('meta[name="description"]')
  if (description && previousMeta.description !== null) description.setAttribute('content', previousMeta.description)
  document.getElementById('cv-jsonld')?.remove()
}

watch(locale, applyMeta)

// ─── Ciclo de vida ────────────────────────────────────────
let sectionObserver

onMounted(() => {
  // ?lang=en permite compartilhar o link já no idioma certo
  const queryLang = route.query.lang
  if (queryLang === 'pt' || queryLang === 'en') locale.value = queryLang
  // ?theme=light / ?theme=dark abre o CV direto no tema escolhido
  const queryTheme = route.query.theme
  if (queryTheme === 'light' || queryTheme === 'dark') theme.value = queryTheme

  previousMeta = {
    lang: document.documentElement.lang,
    description: document.querySelector('meta[name="description"]')?.getAttribute('content') ?? null
  }
  // Depois do afterEach do router, que também define o título
  nextTick(applyMeta)

  window.scrollTo({ top: 0, behavior: 'auto' })

  sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) activeSection.value = entry.target.id
    })
  }, { rootMargin: '-35% 0px -60% 0px' })
  document.querySelectorAll('[data-section]').forEach((el) => sectionObserver.observe(el))

  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})

onUnmounted(() => {
  sectionObserver?.disconnect()
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('scroll', handleScroll)
  document.body.style.overflow = ''
  restoreMeta()
})
</script>

<style scoped>
/*
  Tokens locais do CV: partem da paleta global (--color-bg-dark, --color-yellow,
  --color-light) sem alterá-la para as outras páginas.
*/
.cv {
  --bg: #1E2A3A;
  --bg-deep: #18222F;
  --bar-bg: rgba(30, 42, 58, 0.94);
  --ink: #E5E7EB;
  --ink-soft: #B4BDC9;
  --ink-muted: #8793A3;
  --rule: rgba(148, 163, 184, 0.18);
  --rule-strong: rgba(148, 163, 184, 0.32);
  --gold: var(--color-yellow);
  --logo-filter: brightness(0) invert(1);
  --font-head: "Archivo", "Helvetica Neue", Arial, sans-serif;
  --font-body: "Inter", "Helvetica Neue", Arial, sans-serif;

  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  transition: background-color 0.3s ease, color 0.3s ease;
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.6;
  text-align: left;
  -webkit-font-smoothing: antialiased;
}

/*
  Modo claro: papel cinza-claro com texto azul-marinho. O dourado escurece para
  manter o contraste. As variáveis globais são redefinidas só dentro do CV para
  que o rodapé compartilhado acompanhe o tema.
*/
.cv.theme-light {
  --bg: #F4F5F7;
  --bg-deep: #E4E7EC;
  --bar-bg: rgba(244, 245, 247, 0.94);
  --ink: #1E2A3A;
  --ink-soft: #3B4A5C;
  --ink-muted: #667385;
  --rule: rgba(30, 42, 58, 0.14);
  --rule-strong: rgba(30, 42, 58, 0.3);
  --gold: #A16207;
  --logo-filter: brightness(0);
  --color-bg-dark: #F4F5F7;
  --color-light: #1E2A3A;
}

.wrap {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 32px;
}

/* Espaço da barra fixa */
.cv-main {
  padding-top: 52px;
}

.cv a {
  color: inherit;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.print-only {
  display: none;
}

.cv :focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 3px;
}

/* ─── Barra fixa ──────────────────────────────────────── */
/*
  fixed em vez de sticky: o #app global tem overflow-x: hidden, o que impede o
  sticky de acompanhar a rolagem da janela.
*/
.cv-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: var(--bar-bg);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--rule);
  transition: opacity 0.25s ease, background-color 0.3s ease;
}

/* Depois de rolar, a barra fica discreta; volta ao normal ao passar o mouse ou focar */
.cv-bar.is-scrolled {
  opacity: 0.72;
}

.cv-bar.is-scrolled:hover,
.cv-bar.is-scrolled:focus-within {
  opacity: 1;
}

.bar-tools {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--rule-strong);
  border-radius: 2px;
  background: none;
  color: var(--ink-muted);
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.theme-toggle:hover {
  color: var(--ink);
  border-color: var(--ink-muted);
}

.theme-toggle svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.75;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.bar-inner {
  display: flex;
  align-items: center;
  gap: 28px;
  height: 52px;
}

/* A barra ocupa a largura da janela para caber seções, idioma e tema */
.cv-bar .wrap {
  max-width: none;
}

.bar-name {
  flex: 0 0 auto;
  font-family: var(--font-head);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  letter-spacing: 0.01em;
}

.bar-links {
  display: flex;
  gap: 22px;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0 24px 0 0;
  list-style: none;
  overflow-x: auto;
  scrollbar-width: none;
  /* Esmaece a borda direita: se a lista não couber, indica que ela rola */
  -webkit-mask-image: linear-gradient(90deg, #000 calc(100% - 32px), transparent);
  mask-image: linear-gradient(90deg, #000 calc(100% - 32px), transparent);
}

.bar-links::-webkit-scrollbar {
  display: none;
}

.bar-links a {
  display: block;
  padding: 15px 0 13px;
  border-bottom: 2px solid transparent;
  color: var(--ink-muted);
  font-size: 13px;
  white-space: nowrap;
  text-decoration: none;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.bar-links a:hover {
  color: var(--ink);
}

.bar-links a.active {
  color: var(--ink);
  border-bottom-color: var(--gold);
}

.lang-switch {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-muted);
  font-size: 13px;
}

.lang-switch button {
  padding: 4px 2px;
  border: 0;
  background: none;
  color: var(--ink-muted);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;
}

.lang-switch button:hover,
.lang-switch button[aria-pressed="true"] {
  color: var(--ink);
}

.lang-switch button[aria-pressed="true"] {
  text-decoration: underline;
  text-decoration-color: var(--gold);
  text-underline-offset: 5px;
  text-decoration-thickness: 2px;
}

/* ─── Cabeçalho ───────────────────────────────────────── */
.cv-header {
  display: flex;
  align-items: center;
  gap: 32px;
  padding-top: 56px;
  padding-bottom: 48px;
}

.portrait {
  flex: 0 0 auto;
  width: 112px;
  height: 112px;
  object-fit: cover;
  border-radius: 2px;
}

.identity {
  min-width: 0;
}

.name {
  margin: 0;
  font-family: var(--font-head);
  font-size: 40px;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}

.headline {
  margin: 10px 0 16px;
  color: var(--ink-soft);
  font-size: 18px;
  line-height: 1.4;
}

.headline-sep {
  margin: 0 12px;
  color: var(--gold);
}

.contact-line {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--ink-muted);
  font-size: 14px;
}

/* Separador ao final do item: ao quebrar linha, nenhuma linha começa com "·" */
.contact-line li:not(:last-child)::after {
  content: "·";
  margin: 0 12px;
  color: var(--rule-strong);
}

.contact-line a {
  color: var(--ink);
  text-decoration: underline;
  text-decoration-color: var(--rule-strong);
  text-underline-offset: 4px;
  transition: text-decoration-color 0.2s ease;
}

.contact-line a:hover {
  text-decoration-color: var(--gold);
}

/* ─── Seções ──────────────────────────────────────────── */
.cv-section {
  border-top: 1px solid var(--rule);
}

.section-grid {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 48px;
  padding-top: 48px;
  padding-bottom: 56px;
}

.section-head {
  margin: 0;
  font-family: var(--font-head);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
}

.section-no {
  display: block;
  margin-bottom: 6px;
  color: var(--gold);
  font-size: 13px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.micro-label {
  margin: 0 0 6px;
  color: var(--ink-muted);
  font-family: var(--font-head);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.text-link {
  display: inline-block;
  margin-top: 8px;
  color: var(--ink);
  font-size: 14px;
  text-decoration: underline;
  text-decoration-color: var(--rule-strong);
  text-underline-offset: 4px;
}

.text-link:hover {
  text-decoration-color: var(--gold);
}

/* Resumo */
.summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 48px;
}

.summary-text p {
  margin: 0 0 16px;
  color: var(--ink-soft);
  font-size: 16px;
  line-height: 1.7;
}

.summary-text p:last-child {
  margin-bottom: 0;
}

.summary-text p:first-child {
  color: var(--ink);
  font-size: 18px;
  line-height: 1.6;
}

.facts {
  margin: 0;
  border-left: 1px solid var(--rule);
  padding-left: 24px;
}

.fact + .fact {
  margin-top: 18px;
}

.fact dt {
  color: var(--ink-muted);
  font-size: 13px;
  line-height: 1.4;
}

.fact dd {
  margin: 2px 0 0;
  font-family: var(--font-head);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

/* Experiência e formação (mesmo layout editorial) */
.job {
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 32px;
  padding: 28px 0;
  border-top: 1px solid var(--rule);
}

.job:first-child {
  padding-top: 0;
  border-top: 0;
}

.job:last-child {
  padding-bottom: 0;
}

.job-meta p {
  margin: 0;
}

.job-period {
  font-family: var(--font-head);
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.job-place {
  color: var(--ink-muted);
  font-size: 14px;
}

.job-company {
  margin: 0;
  font-family: var(--font-head);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
}

.job-role {
  margin: 2px 0 10px;
  color: var(--gold);
  font-size: 15px;
  font-weight: 500;
}

.job-summary {
  margin: 0 0 12px;
  color: var(--ink-soft);
}

.job-points,
.group-points {
  margin: 0;
  padding: 0;
  list-style: none;
}

.job-points li,
.group-points li {
  position: relative;
  padding-left: 18px;
  color: var(--ink-soft);
}

.job-points li + li,
.group-points li + li {
  margin-top: 6px;
}

.job-points li::before,
.group-points li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.8em;
  width: 8px;
  height: 1px;
  background: var(--ink-muted);
}

.partners {
  margin-top: 22px;
}

.partner-logos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 28px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}

/* Logos em monocromático claro para conversar com o fundo azul-marinho */
.partner-logos img {
  display: block;
  height: 22px;
  width: auto;
  max-width: 110px;
  object-fit: contain;
  filter: var(--logo-filter);
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.partner-logos li:hover img {
  opacity: 1;
}

/* Projetos */
.project-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  margin-bottom: 36px;
  border-bottom: 1px solid var(--rule);
}

.project-filters button {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: -1px;
  padding: 0 0 12px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  color: var(--ink-muted);
  font-family: var(--font-head);
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.project-filters button:hover {
  color: var(--ink);
}

.project-filters button.active {
  color: var(--ink);
  border-bottom-color: var(--gold);
}

.filter-count {
  color: var(--ink-muted);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.project-group + .project-group {
  margin-top: 56px;
  padding-top: 40px;
  border-top: 1px solid var(--rule);
}

.group-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 24px;
  margin-bottom: 18px;
}

.group-title {
  margin: 0;
  font-family: var(--font-head);
  font-size: 20px;
  font-weight: 600;
}

.group-meta {
  margin: 0;
  color: var(--ink-muted);
  font-size: 14px;
}

.group-role {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 32px;
  margin-bottom: 28px;
  padding: 20px 0;
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
}

.group-role-title {
  margin: 0 0 6px;
  font-family: var(--font-head);
  font-size: 16px;
  font-weight: 600;
}

.group-summary {
  margin: 0;
  color: var(--ink-soft);
  font-size: 15px;
}

.group-points li {
  font-size: 15px;
}

.project-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px 24px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.project {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.project-media {
  display: block;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 2px;
  background: var(--bg-deep);
}

.project-media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.project:hover .project-media img {
  transform: scale(1.03);
}

.project-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
}

.project-name {
  font-family: var(--font-head);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 4px;
  transition: text-decoration-color 0.2s ease;
}

.project:hover .project-name {
  text-decoration-color: var(--gold);
}

.project-year {
  flex: 0 0 auto;
  color: var(--ink-muted);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.project-type {
  margin-top: 2px;
  color: var(--ink-soft);
  font-size: 14px;
  line-height: 1.45;
}

.project-highlight {
  margin-top: 6px;
  color: var(--gold);
  font-size: 13px;
  line-height: 1.4;
}

.project-more {
  margin-top: 10px;
  color: var(--ink-muted);
  font-size: 13px;
  transition: color 0.2s ease;
}

.project:hover .project-more {
  color: var(--ink);
}

/* Competências e idiomas */
.skills,
.languages {
  margin: 0;
}

.skill-row {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 24px;
  padding: 14px 0;
  border-top: 1px solid var(--rule);
}

.skill-row:first-child {
  padding-top: 0;
  border-top: 0;
}

.skill-row dt {
  font-family: var(--font-head);
  font-size: 15px;
  font-weight: 600;
}

.skill-row dd {
  margin: 0;
  color: var(--ink-soft);
  font-size: 15px;
}

.inline-list {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  list-style: none;
}

.inline-list li:not(:last-child)::after {
  content: "·";
  margin: 0 10px;
  color: var(--ink-muted);
}

.cv-footer {
  border-top: 1px solid var(--rule);
}

@media (max-width: 1200px) {
  .bar-name {
    display: none;
  }
}

/* ─── Tablet ──────────────────────────────────────────── */
@media (max-width: 900px) {
  .wrap {
    padding: 0 20px;
  }

  .bar-name {
    display: none;
  }

  .bar-inner {
    gap: 20px;
    height: 48px;
  }

  .cv-main {
    padding-top: 48px;
  }

  .bar-links {
    gap: 18px;
  }

  .bar-links a {
    padding: 13px 0 11px;
  }

  .section-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
    padding-top: 36px;
    padding-bottom: 40px;
  }

  .section-no {
    display: inline;
    margin: 0 10px 0 0;
  }

  .summary {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
  }

  .facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px 20px;
    padding: 20px 0 0;
    border-left: 0;
    border-top: 1px solid var(--rule);
  }

  .fact + .fact {
    margin-top: 0;
  }

  .project-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* ─── Mobile ──────────────────────────────────────────── */
@media (max-width: 640px) {
  /* Foto acima do nome: o texto do cabeçalho usa a largura inteira */
  .cv-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding-top: 28px;
    padding-bottom: 32px;
  }

  .portrait {
    width: 64px;
    height: 64px;
  }

  .name {
    font-size: 26px;
    letter-spacing: 0;
  }

  .headline {
    margin: 8px 0 12px;
    font-size: 15px;
  }

  .headline-sep {
    margin: 0 8px;
  }

  .contact-line {
    font-size: 13px;
  }

  .contact-line li:not(:last-child)::after {
    margin: 0 8px;
  }

  .summary-text p:first-child {
    font-size: 17px;
  }

  .job {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    padding: 24px 0;
  }

  .job-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0 12px;
  }

  .group-role {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }

  .project-list {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
  }

  .skill-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;
  }

  .partner-logos {
    gap: 14px 22px;
  }

  .partner-logos img {
    height: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cv *,
  .cv *::before {
    transition: none !important;
  }
}

/* ─── Impressão (Ctrl+P / Salvar como PDF) ────────────── */
@media print {
  @page {
    size: A4;
    margin: 14mm 14mm 16mm;
  }

  .cv {
    --ink: #111827;
    --ink-soft: #1F2937;
    --ink-muted: #4B5563;
    --rule: #D1D5DB;
    --rule-strong: #9CA3AF;
    --gold: #1E2A3A;
    background: #fff;
    font-size: 10pt;
    line-height: 1.45;
  }

  /*
    No papel ficam só os dados essenciais: a função em cada grupo de projetos
    já está descrita na Experiência, então o bloco "Minha função" sai.
  */
  .cv-bar,
  .cv-footer,
  .contact-line,
  .project-filters,
  .project-media,
  .project-more,
  .group-role,
  .text-link {
    display: none !important;
  }

  .print-only {
    display: block;
  }

  .print-contact {
    margin: 0;
    color: var(--ink-muted);
    font-size: 9.5pt;
  }

  .wrap {
    max-width: none;
    padding: 0;
  }

  .cv-main {
    padding-top: 0;
  }

  .cv-header {
    padding: 0 0 16px;
    gap: 18px;
  }

  .portrait {
    width: 64px;
    height: 64px;
  }

  .name {
    font-size: 20pt;
  }

  .headline {
    margin: 4px 0 6px;
    font-size: 11.5pt;
  }

  .section-grid {
    grid-template-columns: 34mm minmax(0, 1fr);
    gap: 8mm;
    padding: 12px 0 14px;
  }

  .section-head {
    font-size: 11pt;
  }

  .summary {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }

  .summary-text p,
  .summary-text p:first-child {
    margin-bottom: 8px;
    font-size: 10pt;
    line-height: 1.5;
  }

  /* Dados-chave em uma única linha abaixo do resumo */
  .facts {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    padding: 8px 0 0;
    border-left: 0;
    border-top: 1px solid var(--rule);
  }

  .fact + .fact {
    margin-top: 0;
  }

  .fact dt {
    font-size: 8.5pt;
  }

  .fact dd {
    font-size: 9.5pt;
  }

  .job {
    grid-template-columns: 28mm minmax(0, 1fr);
    gap: 6mm;
    padding: 10px 0;
  }

  .job-points li {
    break-inside: avoid;
  }

  .group-head {
    margin-bottom: 6px;
    break-after: avoid;
  }

  .section-head {
    break-after: avoid;
  }

  .job-company,
  .group-title {
    font-size: 12pt;
  }

  .partner-logos img {
    height: 14px;
    filter: grayscale(1);
    opacity: 1;
  }

  .project-group + .project-group {
    margin-top: 14px;
    padding-top: 12px;
  }

  .group-role {
    margin-bottom: 8px;
    padding: 8px 0;
    break-inside: avoid;
  }

  .project-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 8mm;
  }

  .project {
    break-inside: avoid;
  }

  .project-head {
    margin-top: 0;
  }

  .skill-row {
    grid-template-columns: 46mm minmax(0, 1fr);
    padding: 6px 0;
    break-inside: avoid;
  }
}
</style>

<style>
/* Modal teleportado para o body (fora do escopo do componente) */
.cv-modal-backdrop {
  --m-bg: #1E2A3A;
  --m-media: #18222F;
  --m-ink: #E5E7EB;
  --m-soft: #B4BDC9;
  --m-muted: #8793A3;
  --m-gold: #FBBF24;
  --m-rule: rgba(148, 163, 184, 0.18);
  --m-rule-strong: rgba(148, 163, 184, 0.3);
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(10, 16, 26, 0.78);
  text-align: left;
}

.cv-modal {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  width: min(980px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  border: 1px solid var(--m-rule-strong);
  border-radius: 4px;
  background: var(--m-bg);
  color: var(--m-ink);
  font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
  line-height: 1.6;
  scrollbar-width: thin;
}

.cv-modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border: 1px solid var(--m-rule-strong);
  border-radius: 2px;
  background: var(--m-bg);
  color: var(--m-ink);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.cv-modal-close:hover,
.cv-modal-close:focus-visible {
  border-color: var(--m-gold);
  outline: none;
}

.cv-modal-media {
  min-height: 340px;
  background: var(--m-media);
}

.cv-modal-media iframe,
.cv-modal-media img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 340px;
  border: 0;
  object-fit: cover;
}

/* Modo claro do CV */
.cv-modal-backdrop.is-light {
  --m-bg: #F4F5F7;
  --m-media: #E4E7EC;
  --m-ink: #1E2A3A;
  --m-soft: #3B4A5C;
  --m-muted: #667385;
  --m-gold: #A16207;
  --m-rule: rgba(30, 42, 58, 0.14);
  --m-rule-strong: rgba(30, 42, 58, 0.3);
}

/* Post do Instagram: formato vertical, com fundo branco do próprio embed */
.cv-modal-media.instagram {
  display: flex;
  justify-content: center;
  background: #fff;
}

.cv-modal-media.instagram iframe {
  max-width: 540px;
  height: 640px;
}

.cv-modal-body {
  padding: 32px 30px;
}

.cv-modal-context {
  margin: 0 0 6px;
  color: var(--m-muted);
  font-size: 13px;
}

.cv-modal-title {
  margin: 0;
  font-family: "Archivo", "Helvetica Neue", Arial, sans-serif;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.25;
}

.cv-modal-type {
  margin: 4px 0 0;
  color: var(--m-soft);
  font-size: 15px;
}

.cv-modal-highlight {
  margin: 10px 0 0;
  color: var(--m-gold);
  font-size: 14px;
}

.cv-modal-desc {
  margin: 18px 0 0;
  color: var(--m-soft);
  font-size: 15px;
}

.cv-modal-facts {
  margin: 22px 0 0;
  border-top: 1px solid var(--m-rule);
}

.cv-modal-facts > div {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--m-rule);
}

.cv-modal-facts dt {
  color: var(--m-muted);
  font-size: 13px;
}

.cv-modal-facts dd {
  margin: 0;
  font-size: 14px;
}

.cv-modal-facts .spec-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.cv-modal-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 22px;
  margin: 20px 0 0;
  font-size: 14px;
}

.cv-modal-links a {
  color: var(--m-ink);
  text-decoration: underline;
  text-decoration-color: var(--m-rule-strong);
  text-underline-offset: 4px;
}

.cv-modal-links a:hover {
  text-decoration-color: var(--m-gold);
}

.cv-modal-enter-active,
.cv-modal-leave-active {
  transition: opacity 0.2s ease;
}

.cv-modal-enter-from,
.cv-modal-leave-to {
  opacity: 0;
}

@media (max-width: 760px) {
  .cv-modal-backdrop {
    align-items: flex-end;
    padding: 0;
  }

  .cv-modal {
    grid-template-columns: minmax(0, 1fr);
    max-height: 92vh;
    border-radius: 4px 4px 0 0;
  }

  .cv-modal-media,
  .cv-modal-media iframe,
  .cv-modal-media img {
    min-height: 220px;
  }

  .cv-modal-media.instagram iframe {
    height: 560px;
  }

  .cv-modal-body {
    padding: 22px 20px 28px;
  }

  .cv-modal-title {
    font-size: 22px;
  }

  .cv-modal-facts > div {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
  }
}

@media print {
  .cv-modal-backdrop {
    display: none !important;
  }
}
</style>
